import { action, computed, observable, toJS } from 'mobx';
import { first, isArray, isObject, keyBy, last, merge } from 'lodash';
import { LogEvents } from '~/services/fullstory/log-events';
import { createBlockInstance as createCXBlockInstance } from '~/utils/cx-block.utils';
import { isCX as isCXRootStore } from '~/utils/client-experience.utils';
import { CXPage } from '~/components/ClientExperience/OneFlow/models/cx-page.model';
import { TCXBlock } from '~/types/client-experience.types';
import { TRootStore } from '~/types/app.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { Page } from '~/models/page.model';
import type { RootStore } from '~/stores/root.store';
import {
    CommandType,
    DataHandleMethod,
    IBlockBaseContext,
    TBlock,
    TBlockWithVariables
} from '../types/builder.types';
import {
    ITransformedPageResponse,
    IVariableResponse,
    TBlockResponse
} from '../types/server.types';
import { Variable } from './variable.model';
import { createBlockInstance } from '../utils/block.utils';
import { safeMergeValue } from '../utils/data.utils';
import { ServiceSelectionBlock } from './blocks/service-selection-block.model';
import { BOOKABLE_BLOCK_TYPES } from '../common/flows.constants';
import { InvoiceBlock } from './blocks/invoice-block.model';
import { PaymentBlock } from '~/models/blocks/payment-block.model';
import { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import { daysDifference, getAbsoluteDate } from '~/utils/date.utils';
import { ChargeBlock } from '~/models/blocks/charge-block.model';
import type { ContractBlock } from '~/models/blocks/contract-block.model';
import { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';

function isCX(
    revision: FlowRevision<TRootStore>
): revision is FlowRevision<CXRootStore> {
    return isCXRootStore(revision.rootStore);
}

export class FlowRevision<T extends TRootStore> {
    rootStore: T;

    @observable _id: string;
    @observable pageIds: string[];
    @observable pagesMap: Record<string, T extends RootStore ? Page : CXPage> =
        {};
    @observable blocksMap: Record<
        string,
        T extends RootStore ? TBlock : TCXBlock
    > = {};
    @observable variablesMap: Record<string, Variable> = {};

    constructor(
        id: string,
        flowId: string,
        pages: Record<string, ITransformedPageResponse>,
        blocks: Record<string, TBlockResponse>,
        variables: Record<string, IVariableResponse>,
        rootStore: T
    ) {
        this.rootStore = rootStore;
        this._id = id;
        this.pageIds = [];

        Object.keys(pages).forEach(pageId => {
            let newPage;

            const pageResponse = pages[pageId];

            if (isCX(this)) {
                // Page can be null when we don't want to render all the pages like in thumbnails.
                if (!pageResponse) {
                    return;
                }
                newPage = new CXPage(pageResponse, this, this.rootStore);
            } else {
                newPage = new Page(
                    pageResponse,
                    this as FlowRevision<RootStore>,
                    this.rootStore as RootStore
                ); // TODO: why does the else of isCX not know that the rootStore is a RootStore?
            }

            this.addPage(newPage);
        });

        Object.keys(blocks).forEach(blockId => {
            const blockResponse = blocks[blockId];
            let newBlock;

            // TODO: fix types for createXXBlockInstance
            if (isCX(this)) {
                newBlock = createCXBlockInstance(
                    blockResponse,
                    this,
                    this.rootStore
                );
            } else {
                newBlock = createBlockInstance<TBlock>(
                    blockResponse,
                    this as FlowRevision<RootStore>,
                    this.rootStore as RootStore
                );
            }
            this.addBlock(newBlock);
        });

        this.addVariables(Object.values(variables));
    }

    // Pages
    // #region pages
    @computed
    get pages() {
        return this.pageIds
            .map(pageId => this.pagesMap[pageId])
            .filter(page => page !== undefined)
            .sort((a, b) => a.order - b.order);
    }

    @action
    addPage(page: T extends RootStore ? Page : CXPage) {
        this.pageIds.push(page.page_id);
        this.pagesMap[page.page_id] = page;

        if (isCX(this)) {
            (page as CXPage).setFlowRevision(this as FlowRevision<CXRootStore>);
        } else {
            (page as Page).setFlowRevision(this as FlowRevision<RootStore>);
        }
    }

    @action
    removePage(pageId: string) {
        this.pageIds = this.pageIds.filter(id => id !== pageId);
        delete this.pagesMap[pageId];
    }

    @action
    updatePage(
        pageId: string,
        data: Partial<T extends RootStore ? Page : CXPage>
    ) {
        this.pagesMap[pageId] = merge(this.pagesMap[pageId], data);
    }

    @action
    setPageData(pageId: string, data: Partial<Page>) {
        this.pagesMap[pageId] = safeMergeValue(this.pagesMap[pageId], data);
    }

    // #endregion

    // Blocks

    // #region blocks
    // TODO: refactor this to be the same between cx and non cx (what tests do we need to do this refactor?)
    @computed
    get blocks(): (T extends RootStore ? TBlock : TCXBlock)[] {
        if (isCX(this)) {
            const pagesIdsHash = keyBy(this.pages, 'page_id');

            return Object.values(this.blocksMap).filter(
                x => pagesIdsHash[x.page_id]
            );
        }

        return this.pages.reduce((acc, page) => acc.concat(page.blocks), []);
    }

    getBlock(blockId: string) {
        return this.blocksMap[blockId] as TBlock; // this is only used from the builder
    }

    @action
    addBlock(block: T extends RootStore ? TBlock : TCXBlock) {
        // @ts-ignore
        block.setFlowRevision(this);
        this.blocksMap[block.block_id] = block;
    }

    @action
    deleteBlock(blockId: string) {
        const block = this.blocksMap[blockId];
        if (block?.connected_variable_id) {
            delete this.variablesMap[block.connected_variable_id];
        }
        delete this.blocksMap[blockId];
    }

    @action
    setBlockData(
        blockId: string,
        data: Partial<TBlockResponse>,
        dataHandleMethod: DataHandleMethod = DataHandleMethod.merge
    ) {
        this.blocksMap[blockId] =
            dataHandleMethod === DataHandleMethod.merge
                ? safeMergeValue(this.blocksMap[blockId], data)
                : Object.assign(this.blocksMap[blockId], data);
    }

    // #endregion

    // Variables
    // #region variables

    @computed
    get variables() {
        return Object.keys(this.variablesMap).map(
            variableId => this.variablesMap[variableId]
        );
    }

    @action
    addVariables(variables: IVariableResponse[]) {
        variables?.forEach(variable => {
            this.variablesMap[variable.variable_id] = new Variable(
                { ...variable },
                this.rootStore
            );
        });
    }

    @action
    updateVariables(variables: IVariableResponse[]) {
        this.removeVariables(variables);
        this.addVariables(variables);
    }

    @action
    removeVariables(variables: { variable_id: string }[]) {
        variables.forEach(variable => {
            const variableId = variable.variable_id;

            delete this.variablesMap[variableId];
        });
    }

    @action
    setVariableValue(
        variableId: string,
        value: any,
        safeMerge: boolean = true
    ) {
        if (!isObject(value) && !isArray(value)) {
            this.variablesMap[variableId]?.setValue(value);
        } else {
            this.variablesMap[variableId]?.setValue(
                safeMerge
                    ? safeMergeValue(this.variablesMap[variableId].value, value)
                    : value
            );
        }
    }

    @action
    updateBlocksWithConnectedVariableIds(variables) {
        const { fullstoryService } = this.rootStore;

        variables.forEach(variable => {
            const variableFromStore = this.blocksMap[
                variable.connected_block_id
            ] as TBlockWithVariables;

            if (
                !variableFromStore &&
                this.isConnectedBlockDeletedInFuture(
                    variable.connected_block_id
                )
            ) {
                fullstoryService.warn(
                    LogEvents.VariableRaceConditionFound(
                        variable?.variable_id,
                        variable?.connected_block_id
                    )
                );

                return;
            }

            variableFromStore.connected_variable_id = variable.variable_id;
        });
    }

    @action
    updateBlocksWithDisconnectedVariableIds(variables) {
        variables.forEach(variable => {
            const blockToUpdate = this.blocksMap[variable.connected_block_id];

            if (!blockToUpdate) {
                return;
            }

            (blockToUpdate as TBlockWithVariables).connected_variable_id = null;
        });
    }

    // #endregion

    @computed
    get linkedFields() {
        return this.pages
            .map(page => page.linkedFields)
            .reduce((allLinkedFields, pageLinkedFields) => {
                return allLinkedFields.concat(pageLinkedFields);
            });
    }

    @computed
    get paymentsBlocksCount() {
        return this.paymentsBlocksSortedByIndex?.length;
    }

    @computed
    get chargeBlock() {
        return this.blocks.find(
            block => block.type === 'charge_block'
        ) as ChargeBlock;
    }

    @computed
    get isRecurringInvoice() {
        return this.invoiceContainerBlock?.isRecurringInvoice;
    }

    @computed
    get paymentsBlocksSortedByDueDate() {
        return this.paymentsBlocks
            .filter(block => block.paymentDueDate)
            .sort(
                (a, b) =>
                    new Date(a.paymentDueDate).getTime() -
                    new Date(b.paymentDueDate).getTime()
            );
    }

    @computed
    get paymentsBlocksSortedByIndex() {
        return this.paymentsBlocks.sort(
            (a, b) => a.paymentIndex - b.paymentIndex
        );
    }

    @computed
    get duePaymentsBlocksCount() {
        return this.paymentsBlocksSortedByDueDate?.length;
    }

    // test needed
    @computed
    get lastPaidPaymentBlock() {
        return last(this.paidPayments);
    }

    @computed
    get nonCompletedMilestonePayments() {
        return this.paymentsBlocks?.filter(
            blk => blk.isMilestone && !blk.milestoneCompletedOn
        );
    }

    @computed
    get pendingPayments() {
        return this.paymentsBlocksSortedByIndex.filter(
            payment => payment.isPending
        );
    }

    @computed
    get hasPendingPayments() {
        return this.pendingPayments.length > 0;
    }

    @computed
    get lastPendingPayment() {
        return first(this.pendingPayments);
    }

    get nextUnpaidPaymentIncludeNonCompletedMilestones() {
        return this.paymentsBlocksSortedByIndex.find(
            payment => !payment?.is_paid && !payment.is_refunded
        );
    }

    @computed
    get nextUnpaidPayment() {
        return this.paymentsBlocksSortedByIndex.find(
            payment =>
                !(
                    payment.is_paid ||
                    payment.is_refunded ||
                    (payment.isMilestone && !payment.milestoneCompletedOn)
                )
        );
    }

    @computed
    get nextUnpaidPaymentSkipOverdue() {
        return this.paymentsBlocksSortedByIndex.find(
            payment =>
                !(
                    payment.isOverDue ||
                    payment.is_paid ||
                    payment.is_refunded ||
                    (payment.isMilestone && !payment.milestoneCompletedOn)
                )
        );
    }

    // test needed
    @computed
    get overduePayments() {
        if (!this.paymentsBlocksSortedByIndex) {
            return [];
        }
        return this.paymentsBlocksSortedByIndex.filter(payment => {
            const dueDate = getAbsoluteDate(payment.paymentDueDate);
            const isPaid = payment.is_paid;
            return !isPaid && daysDifference(dueDate, new Date()) < 0;
        });
    }

    @computed
    get isSomePaymentPaid() {
        return this.paymentsBlocksSortedByIndex.some(p => p.is_paid);
    }

    @computed
    get isAllPaymentsPaid() {
        return this.paymentsBlocksSortedByIndex.every(p => p.is_paid);
    }

    @computed
    get isAllPaymentsNotRequestedMilestone() {
        return (
            this.paymentsBlocksSortedByIndex?.length ===
            this.nonCompletedMilestonePayments?.length
        );
    }

    @computed
    get upcomingPayment() {
        const nextUnPaid = this.nextUnpaidPayment;
        const nextUnPaidIncludeNonCompleted =
            this.nextUnpaidPaymentIncludeNonCompletedMilestones;

        return nextUnPaid || nextUnPaidIncludeNonCompleted;
    }

    @computed
    get upcomingPaymentNonOverdue() {
        const nextUnPaid = this.nextUnpaidPaymentSkipOverdue;
        const nextUnPaidIncludeNonCompleted =
            this.nextUnpaidPaymentIncludeNonCompletedMilestones;

        return nextUnPaid || nextUnPaidIncludeNonCompleted;
    }

    @computed
    get paymentsBlocks(): (T extends RootStore
        ? PaymentBlock
        : CXPaymentBlock)[] {
        return this.blocks.filter(
            b => b?.isPaymentBlock
        ) as (T extends RootStore ? PaymentBlock : CXPaymentBlock)[];
    }

    @computed
    get isRecurringDisabled() {
        return (
            this.isRecurringInvoice &&
            !!this.invoiceContainerBlock?.invoiceRecurringScheduleBlock
                ?.connectedVariable &&
            !this.invoiceContainerBlock?.invoiceRecurringScheduleBlock
                ?.connectedVariable.value.is_active
        );
    }

    @computed
    get invoiceContainerBlock() {
        return this.blocks.find(b => b.isInvoiceContainerBlock) as InvoiceBlock;
    }

    @computed
    get contractBlocks() {
        return this.blocks.filter(b => b.isContractBlock) as ContractBlock[];
    }

    @computed
    get scheduleContainerBlock() {
        return this.blocks.find(
            b => b.isScheduleContainerBlock
        ) as ScheduleContainerBlock;
    }

    invoiceServiceSelectionBlocks(invoice_block_id) {
        return this.blocks
            .filter(
                blk =>
                    blk.type === 'service_selection_block' &&
                    (blk as ServiceSelectionBlock).invoice_block_id ===
                        invoice_block_id
            )
            .sort((a, b) => a.page.order - b.page.order);
    }

    @computed
    get payContainerBlock() {
        return this.blocks.find(b => b.isPayContainerBlock);
    }

    @computed
    get pageWithInvoice() {
        return this.pages.find(p => !!p.invoiceContainerBlock);
    }

    @computed
    get pageWithPay() {
        return this.pages.find(p => !!p.payContainerBlock);
    }

    @computed
    get isBookable() {
        return this.blocks.some(block =>
            BOOKABLE_BLOCK_TYPES.includes(block.type)
        );
    }

    @computed
    get hasInvoiceBlock() {
        return !!this.invoiceContainerBlock;
    }

    @computed
    get hasContractBlock() {
        return !!this.contractBlocks.length;
    }

    @computed
    get hasPaidPayments() {
        return this.invoiceContainerBlock?.hasPaidPayments;
    }

    @computed
    get hasNonManualPaidPayments() {
        return this.invoiceContainerBlock?.hasNonManualPaidPayments;
    }

    get lastPageId() {
        return last(this.pages).page_id;
    }

    @computed
    get paidPayments() {
        return this.paymentsBlocksSortedByDueDate.filter(
            payment => payment.is_paid
        );
    }

    get unpaidPayments() {
        return this.paymentsBlocksSortedByDueDate.filter(
            payment => !payment.is_paid
        );
    }

    get paidBalance() {
        return this.paidPayments.reduce(
            (acc, payment) => acc + payment.totalAmount,
            0
        );
    }

    get remainingBalance() {
        return this.unpaidPayments.reduce(
            (acc, payment) => acc + payment.totalAmount,
            0
        );
    }

    submittedPageIdsByClientId(clientId: string) {
        return this.pages
            .filter(page =>
                page.submissions?.some(
                    submission => submission.user_id === clientId
                )
            )
            .map(page => page.page_id);
    }

    viewedPagesByClientId(clientId: string) {
        return this.pages.filter(page =>
            page.views?.some(view => view.user_id === clientId)
        );
    }

    lastViewedPageByClientId(clientId: string) {
        const viewedPagesByTime = this.viewedPagesByClientId(clientId).sort(
            (pageA, pageB) => {
                const pageAViewTime = new Date(
                    pageA.views.find(
                        view => view.user_id === clientId
                    ).performed_at
                ).getTime();
                const pageBViewTime = new Date(
                    pageB.views.find(
                        view => view.user_id === clientId
                    ).performed_at
                ).getTime();

                return pageBViewTime - pageAViewTime;
            }
        );

        return viewedPagesByTime[0];
    }

    getIsLastPageViewedByClientId(clientId: string) {
        return this.viewedPagesByClientId(clientId).some(
            page => page.page_id === this.lastPageId
        );
    }

    containsLinkedQuestionType(questionTypeKey) {
        return this.linkedFields.indexOf(questionTypeKey) !== -1;
    }

    clone(flowId: string) {
        const clonedRevision = toJS(this);

        // Remove flowRevision from pages/blocks before cloning
        // to prevent circular dependencies
        const clonedPages = Object.keys(clonedRevision.pagesMap).reduce(
            (acc, id) => {
                const { flowRevision, ...page } = clonedRevision.pagesMap[id];
                acc[id] = {
                    ...page
                };
                return acc;
            },
            {}
        );
        const clonedBlocks = Object.keys(clonedRevision.blocksMap).reduce(
            (acc, id) => {
                const { flowRevision, ...block } = clonedRevision.blocksMap[id];
                acc[id] = {
                    ...block
                };
                return acc;
            },
            {}
        );
        const clonedVariables = toJS(clonedRevision.variablesMap);

        const newRevision = new FlowRevision<RootStore>(
            clonedRevision._id,
            flowId,
            clonedPages,
            // toJS changes the blocks from instances to POJOs so the types aren't inferred correctly
            // @ts-ignore
            clonedBlocks,
            clonedVariables,
            this.rootStore as RootStore
        );

        return newRevision;
    }

    private isConnectedBlockDeletedInFuture(block_id) {
        if (isCX(this)) {
            return false;
        }

        const {
            actionsManager: { actionsStack }
        } = this.rootStore as RootStore;
        const waitingActions = actionsStack.getWaitingActions();

        return (
            block_id &&
            waitingActions.some(waitingAction =>
                (waitingAction.ctx as IBlockBaseContext)?.commands.some(
                    command =>
                        command.type === CommandType.Delete &&
                        (command.data as TBlock)?.block_id === block_id
                )
            )
        );
    }
}
